<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageContext } from '../../provider.js';
import { MESSAGE_VARIANTS } from '../../constants';

const props = defineProps({
  ad: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const { variant } = useMessageContext();

const darkSurfaceVariants = [
  MESSAGE_VARIANTS.AGENT,
  MESSAGE_VARIANTS.BOT,
  MESSAGE_VARIANTS.TEMPLATE,
];

const isDarkSurfaceVariant = computed(() =>
  darkSurfaceVariants.includes(variant.value)
);

const rawAd = computed(() => props.ad || {});

const normalizedAd = computed(() => {
  const raw = rawAd.value || {};

  let thumbnail =
    raw.thumbnailDataUri ||
    raw.thumbnailDataURL ||
    raw.thumbnailUrl ||
    raw.thumbnailURL ||
    '';

  if (!thumbnail && raw.thumbnail) {
    const stringified = raw.thumbnail.toString();
    thumbnail = stringified.startsWith('data:')
      ? stringified
      : `data:image/png;base64,${stringified}`;
  }

  if (!thumbnail && raw.thumbnail_data_uri) {
    thumbnail = raw.thumbnail_data_uri;
  }

  return {
    title: raw.title || '',
    body: raw.body || '',
    sourceUrl: raw.sourceUrl || raw.source_url || raw.sourceURL || '',
    sourceId: raw.sourceId || raw.source_id || raw.sourceID || '',
    greetingMessageBody:
      raw.greetingMessageBody || raw.greeting_message_body || '',
    sourceApp: raw.sourceApp || raw.source_app || '',
    sourceType: raw.sourceType || raw.source_type || '',
    thumbnail,
  };
});

const platformRaw = computed(
  () => normalizedAd.value.sourceApp || normalizedAd.value.sourceType || ''
);

const headerKey = computed(() => {
  const normalized = platformRaw.value
    ? platformRaw.value.toString().toLowerCase()
    : '';
  if (normalized.includes('facebook')) return 'FACEBOOK_HEADER';
  if (normalized.includes('instagram')) return 'INSTAGRAM_HEADER';
  if (normalized.includes('whatsapp')) return 'WHATSAPP_HEADER';
  return 'DEFAULT_HEADER';
});

const headerLabel = computed(() =>
  t(`CONVERSATION.EXTERNAL_AD.${headerKey.value}`)
);

const platformName = computed(() => {
  const raw = platformRaw.value;
  if (!raw) return '';

  const normalized = raw.toString().trim();
  if (!normalized) return '';

  const lower = normalized.toLowerCase();
  if (lower === 'facebook') return 'Facebook';
  if (lower === 'instagram') return 'Instagram';
  if (lower === 'whatsapp') return 'WhatsApp';

  return normalized;
});

const thumbnail = computed(() => normalizedAd.value.thumbnail);

const linkLabel = computed(() => {
  if (!normalizedAd.value.sourceUrl) return '';
  const platform = platformName.value;
  if (platform) {
    return t('CONVERSATION.EXTERNAL_AD.LINK_LABEL', { platform });
  }
  return t('CONVERSATION.EXTERNAL_AD.VIEW_AD');
});

const headerTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/60' : 'text-n-slate-9';
});

const titleTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white' : 'text-n-slate-12';
});

const bodyTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/80' : 'text-n-slate-11';
});

const linkTextClass = computed(() => {
  return isDarkSurfaceVariant.value
    ? 'text-white underline decoration-white/40 hover:decoration-white'
    : 'text-n-brand-12';
});

const sourceIdContainerClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/70' : 'text-n-slate-10';
});

const sourceIdLabelTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/80' : 'text-n-slate-11';
});

const greetingLabelTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/60' : 'text-n-slate-9';
});

const greetingBodyTextClass = computed(() => {
  return isDarkSurfaceVariant.value ? 'text-white/80' : 'text-n-slate-11';
});
</script>

<template>
  <article
    class="flex flex-col gap-3 rounded-xl border border-n-alpha-4 bg-n-alpha-2 p-4"
  >
    <div class="flex items-start gap-3">
      <img
        v-if="thumbnail"
        :src="thumbnail"
        alt=""
        class="h-12 w-12 rounded-lg object-cover"
      />
      <div class="flex-1">
        <p
          class="text-xs font-semibold uppercase tracking-wide"
          :class="headerTextClass"
        >
          {{ headerLabel }}
        </p>
        <p
          v-if="normalizedAd.title"
          class="mt-1 text-base font-semibold"
          :class="titleTextClass"
        >
          {{ normalizedAd.title }}
        </p>
      </div>
    </div>
    <p
      v-if="normalizedAd.body"
      class="whitespace-pre-line text-sm leading-5"
      :class="bodyTextClass"
    >
      {{ normalizedAd.body }}
    </p>
    <a
      v-if="normalizedAd.sourceUrl"
      :href="normalizedAd.sourceUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="text-sm font-medium transition-colors"
      :class="linkTextClass"
    >
      {{ linkLabel }}
    </a>
    <div
      v-if="normalizedAd.sourceId"
      class="text-xs"
      :class="sourceIdContainerClass"
    >
      <span class="font-medium" :class="sourceIdLabelTextClass">
        {{ $t('CONVERSATION.EXTERNAL_AD.AD_ID_LABEL') }}:
      </span>
      <span class="ml-1 font-mono">{{ normalizedAd.sourceId }}</span>
    </div>
    <div
      v-if="normalizedAd.greetingMessageBody"
      class="rounded-lg bg-n-alpha-3 px-3 py-2"
    >
      <p
        class="text-[11px] font-semibold uppercase tracking-wide"
        :class="greetingLabelTextClass"
      >
        {{ $t('CONVERSATION.EXTERNAL_AD.GREETING_MESSAGE_LABEL') }}
      </p>
      <p
        class="mt-1 whitespace-pre-line text-sm leading-5"
        :class="greetingBodyTextClass"
      >
        {{ normalizedAd.greetingMessageBody }}
      </p>
    </div>
  </article>
</template>
